<template>
    <div class="modal-card consolidated-modal">
        <header class="modal-card-head has-background-white">
            <h3 class="modal-card-title has-text-centered">
                Miembros de {{ consolidatedName }}
            </h3>
            <span class="head-tag">Consolidada · solo lectura</span>
            <button class="delete" aria-label="Cerrar Modal" @click="$emit('close')"></button>
        </header>

        <!-- PASO 0: edición (miembros actuales | buscador de cartera) -->
        <template v-if="step === 'edit'">
            <section class="section-body">
                <div class="col col-members">
                    <p class="col-eyebrow">Miembros ({{ membersCountLabel }})</p>
                    <div class="col-scroll">
                        <div v-if="memberView.length === 0" class="empty-state">
                            Sin miembros todavía. Busca a la derecha y agrega con «Agregar».
                        </div>
                        <div
                            v-for="item in memberView"
                            :key="item.company.company_id"
                            class="company-row"
                            :class="{ 'is-added': item.state === 'nueva', 'is-removed': item.state === 'baja' }"
                        >
                            <div class="company-row__info">
                                <span class="company-row__name">{{ item.company.name }}</span>
                                <span class="company-row__meta">{{ item.company.rut }}</span>
                            </div>
                            <span v-if="item.state === 'nueva'" class="state-tag state-tag--add">nueva</span>
                            <button
                                v-if="item.state === 'actual'"
                                type="button"
                                class="row-action row-action--remove"
                                @click="toggleRemove(item.company.company_id)"
                            >
                                Quitar
                            </button>
                            <button
                                v-else
                                type="button"
                                class="row-action"
                                @click="item.state === 'baja' ? toggleRemove(item.company.company_id) : undoAdd(item.company.company_id)"
                            >
                                Deshacer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="col col-search">
                    <p class="col-eyebrow">Agregar empresas de tu cartera</p>
                    <input
                        type="text"
                        class="search-input"
                        v-model="filterInput"
                        placeholder="Buscar por nombre o RUT"
                        aria-label="Buscar empresas para agregar"
                    />
                    <div class="col-scroll">
                        <div v-if="searchResults.length === 0" class="empty-state">
                            <template v-if="filterCompany.trim()">Sin resultados para «{{ filterCompany.trim() }}».</template>
                            <template v-else>No hay más empresas para agregar.</template>
                        </div>
                        <div v-for="company in searchResults" :key="company.c_id" class="company-row">
                            <div class="company-row__info">
                                <span class="company-row__name">{{ company.name }}</span>
                                <span class="company-row__meta">{{ company.rut }}</span>
                            </div>
                            <button
                                type="button"
                                class="row-action row-action--add"
                                @click="addCompany(company)"
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-card-foot bt-none has-background-white">
                <div class="pending-tray" aria-live="polite">
                    <template v-if="pendingCount">
                        <button
                            v-for="chip in trayChips"
                            :key="chip.key"
                            type="button"
                            class="tray-chip"
                            :class="chip.type === 'add' ? 'tray-chip--add' : 'tray-chip--remove'"
                            :title="'Deshacer: ' + chip.label"
                            @click="undoChip(chip)"
                        >
                            {{ chip.type === 'add' ? '+' : '−' }} {{ chip.label }}
                        </button>
                        <button type="button" class="tray-discard" @click="discardChanges()">
                            Descartar todo
                        </button>
                    </template>
                    <span v-else class="tray-empty">Sin cambios pendientes</span>
                </div>
                <div class="foot-actions">
                    <button class="button" @click="$emit('close')" aria-label="Cerrar Modal">
                        Volver
                    </button>
                    <button
                        class="button is-primary"
                        :disabled="loading || pendingCount === 0"
                        @click="revisarCambios()"
                    >
                        Revisar cambios
                    </button>
                </div>
            </footer>
        </template>

        <!-- PASO 1 de 2: verificar empresas y sus reglas -->
        <template v-else-if="step === 'members'">
            <section class="verify-section">
                <p class="verify-step">Verificación 1 de 2</p>
                <p class="verify-title">Revisa las empresas y sus reglas</p>
                <p class="verify-hint">
                    Cada empresa conserva sus propias reglas dentro de la empresa consolidada.
                    Verifica que sean las correctas antes de continuar.
                </p>
                <div class="col-scroll verify-scroll">
                    <div
                        v-for="member in preview.members"
                        :key="member.company_id"
                        class="company-row verify-row"
                        :class="{ 'is-added': member.change === 'alta', 'is-removed': member.change === 'baja' }"
                    >
                        <div class="company-row__info">
                            <span class="company-row__name">
                                {{ member.name }}
                                <span class="change-tag" :class="'change-tag--' + member.change">{{ member.change }}</span>
                            </span>
                            <span class="company-row__meta">{{ member.rut }}</span>
                            <span class="company-row__meta">
                                {{ reglaDe(member) }} · {{ tiposDe(member) }} · SII: {{ member.sii_status || '—' }}
                            </span>
                            <span class="company-row__meta">{{ datosDe(member) }}</span>
                        </div>
                    </div>
                </div>
                <p v-if="preview.intercompany_pairs > 0" class="verify-warning">
                    Hay {{ preview.intercompany_pairs }} relación(es) entre las empresas seleccionadas:
                    los documentos entre ellas aparecerán dos veces (venta en una, compra en la otra).
                </p>
            </section>
            <footer class="modal-card-foot bt-none has-background-white">
                <div class="foot-actions foot-actions--spread">
                    <button class="button" @click="step = 'edit'">Volver a editar</button>
                    <button class="button is-primary" @click="step = 'users'">Continuar</button>
                </div>
            </footer>
        </template>

        <!-- PASO 2 de 2: verificar usuarios (quiénes verán los datos) -->
        <template v-else>
            <section class="verify-section">
                <p class="verify-step">Verificación 2 de 2</p>
                <p class="verify-title">Revisa quiénes verán estos datos</p>
                <p class="verify-hint verify-hint--strong">
                    Estos usuarios verán los datos de todas las empresas seleccionadas.
                    Si alguien no corresponde, quítalo de la empresa consolidada antes de aplicar.
                </p>
                <div class="col-scroll verify-scroll">
                    <div v-if="preview.users.length === 0" class="empty-state">
                        La empresa consolidada todavía no tiene usuarios: nadie verá estos datos
                        hasta que asignes uno.
                    </div>
                    <div v-for="user in preview.users" :key="user.user_id" class="company-row">
                        <div class="company-row__info">
                            <span class="company-row__name">{{ user.email }}</span>
                            <span class="company-row__meta">
                                {{ user.name || 'Sin nombre' }} · {{ user.role || 'Sin rol' }}
                                <template v-if="!user.is_active"> · inactivo</template>
                            </span>
                        </div>
                    </div>
                </div>
            </section>
            <footer class="modal-card-foot bt-none has-background-white">
                <div class="foot-actions foot-actions--spread">
                    <button class="button" @click="step = 'members'">Volver</button>
                    <button class="button is-primary" :disabled="loading" @click="confirmar()">
                        Confirmar y aplicar
                    </button>
                </div>
            </footer>
        </template>
    </div>
</template>

<script>
import {
    allCompanies,
    getConsolidatedMembers,
    saveConsolidatedMembers,
} from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";

const MAX_RESULTS = 60;

export default {
    name: "ManageConsolidatedMembers",
    props: {
        consolidatedId: {
            type: Number,
            required: true,
        },
        consolidatedName: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            // 'edit' → 'members' (verificación 1) → 'users' (verificación 2).
            step: "edit",
            currentMembers: [],
            raw_companies_list: [],
            pendingAdd: [],
            pendingRemove: [],
            // Preview server-driven vigente (miembros con reglas + usuarios +
            // preview_token). Se pisa entero en cada revisión y en cada 409.
            preview: null,
            filterInput: "",
            filterCompany: "",
            filterTimer: null,
            serverResults: [],
            searchSeq: 0,
            loading: false,
        };
    },
    computed: {
        memberView() {
            const nameOf = (id) => {
                const company = this.raw_companies_list.find((c) => c.c_id === id);
                return {
                    company_id: id,
                    name: company ? company.name : `#${id}`,
                    rut: company ? company.rut : "",
                };
            };
            const added = this.pendingAdd.map((id) => ({
                company: nameOf(id),
                state: "nueva",
            }));
            const current = this.currentMembers.map((member) => ({
                company: member,
                state: this.pendingRemove.includes(member.company_id)
                    ? "baja"
                    : "actual",
            }));
            return [...added, ...current];
        },
        membersCountLabel() {
            const now = this.currentMembers.length;
            const after = now + this.pendingAdd.length - this.pendingRemove.length;
            return after === now ? String(now) : `${now} → ${after}`;
        },
        searchResults() {
            const term = this.filterCompany.trim().toUpperCase();
            const memberIds = new Set(
                this.currentMembers.map((member) => member.company_id),
            );
            const excluded = (company) =>
                memberIds.has(company.c_id) ||
                this.pendingAdd.includes(company.c_id) ||
                company.c_id === this.consolidatedId;
            let list = this.raw_companies_list.filter((c) => !excluded(c));
            if (term) {
                list = list.filter((c) => (c.all_text || "").includes(term));
                const seen = new Set(list.map((c) => c.c_id));
                const extras = this.serverResults.filter(
                    (c) => !excluded(c) && !seen.has(c.c_id),
                );
                list = [...list, ...extras];
            }
            return list.slice(0, MAX_RESULTS);
        },
        pendingCount() {
            return this.pendingAdd.length + this.pendingRemove.length;
        },
        trayChips() {
            const nameOf = (id) => {
                const member = this.currentMembers.find((m) => m.company_id === id);
                if (member) {
                    return member.name;
                }
                const company = this.raw_companies_list.find((c) => c.c_id === id);
                return company ? company.name : `#${id}`;
            };
            return [
                ...this.pendingAdd.map((id) => ({
                    key: `add-${id}`, id, type: "add", label: nameOf(id),
                })),
                ...this.pendingRemove.map((id) => ({
                    key: `remove-${id}`, id, type: "remove", label: nameOf(id),
                })),
            ];
        },
    },
    methods: {
        reglaDe(member) {
            if (!member.has_ledger) {
                return "Solo SII";
            }
            return member.ledger_cutoff_date
                ? `Mayor hasta ${member.ledger_cutoff_date}`
                : "Con mayor";
        },
        tiposDe(member) {
            return (member.business_types || []).join(", ") || "Sin tipo";
        },
        datosDe(member) {
            if (!member.documents) {
                return "Sin documentos";
            }
            return `${member.documents} documentos (${member.first_document_at} → ${member.last_document_at})`;
        },
        toggleRemove(companyId) {
            const index = this.pendingRemove.indexOf(companyId);
            if (index === -1) {
                this.pendingRemove.push(companyId);
            } else {
                this.pendingRemove.splice(index, 1);
            }
        },
        addCompany(company) {
            if (!this.raw_companies_list.some((c) => c.c_id === company.c_id)) {
                this.raw_companies_list.push(company);
            }
            if (!this.pendingAdd.includes(company.c_id)) {
                this.pendingAdd.push(company.c_id);
            }
        },
        undoAdd(companyId) {
            const index = this.pendingAdd.indexOf(companyId);
            if (index !== -1) {
                this.pendingAdd.splice(index, 1);
            }
        },
        undoChip(chip) {
            if (chip.type === "add") {
                this.undoAdd(chip.id);
            } else {
                const index = this.pendingRemove.indexOf(chip.id);
                if (index !== -1) {
                    this.pendingRemove.splice(index, 1);
                }
            }
        },
        discardChanges() {
            this.pendingAdd = [];
            this.pendingRemove = [];
        },
        async loadMembers() {
            try {
                const { data } = await getConsolidatedMembers(this.consolidatedId);
                // El GET sin cambios trae los miembros actuales ('actual').
                this.currentMembers = data.members.filter(
                    (member) => member.change !== "baja",
                );
            } catch (error) {
                consoleHandlerError({
                    error,
                    customDefaultMessage: "No se pudieron cargar los miembros",
                    empty: true,
                });
            }
        },
        // Primer POST (sin confirm): el server valida y devuelve el preview
        // que alimenta las dos pantallas de verificación.
        async revisarCambios() {
            if (this.pendingCount === 0) {
                return;
            }
            this.loading = true;
            try {
                const { data } = await saveConsolidatedMembers(
                    this.consolidatedId, this.pendingAdd, this.pendingRemove,
                );
                this.preview = data;
                this.step = "members";
            } catch (error) {
                consoleHandlerError({
                    error,
                    customDefaultMessage: "No se pudieron revisar los cambios",
                    empty: true,
                });
            } finally {
                this.loading = false;
            }
        },
        // Segundo POST (confirm + preview_token): si el estado cambió entre
        // medio el server responde 409 con el preview fresco → se vuelve a la
        // verificación 1 con los datos nuevos, sin aplicar nada.
        async confirmar() {
            this.loading = true;
            try {
                await saveConsolidatedMembers(
                    this.consolidatedId,
                    this.pendingAdd,
                    this.pendingRemove,
                    true,
                    this.preview.preview_token,
                );
                notificate(
                    "<strong>Miembros actualizados</strong>",
                    "is-success is-light",
                );
                this.pendingAdd = [];
                this.pendingRemove = [];
                this.preview = null;
                this.step = "edit";
                await this.loadMembers();
                this.$emit("saved");
            } catch (error) {
                const response = error && error.response;
                if (response && response.status === 409) {
                    this.preview = response.data;
                    this.step = "members";
                    notificate(
                        "Los miembros o los usuarios cambiaron mientras revisabas. Verifica de nuevo antes de aplicar.",
                        "is-warning is-light",
                    );
                } else {
                    consoleHandlerError({
                        error,
                        customDefaultMessage: "No se pudieron aplicar los cambios",
                        empty: true,
                    });
                }
            } finally {
                this.loading = false;
            }
        },
    },
    mounted() {
        this.loadMembers();
        allCompanies().then((response) => {
            this.raw_companies_list = response.data;
        }).catch((error) => {
            consoleHandlerError({ error, empty: true });
        });
    },
    beforeDestroy() {
        clearTimeout(this.filterTimer);
    },
    watch: {
        filterInput(value) {
            clearTimeout(this.filterTimer);
            this.filterTimer = setTimeout(() => {
                this.filterCompany = value;
            }, 180);
        },
        filterCompany(value) {
            const term = (value || "").trim();
            const seq = ++this.searchSeq;
            if (term.length < 2) {
                this.serverResults = [];
                return;
            }
            allCompanies(true, term).then((response) => {
                if (seq === this.searchSeq) {
                    this.serverResults = response.data;
                }
            }).catch(() => {
                // Búsqueda auxiliar: si falla, el filtro local sigue sirviendo.
            });
        },
    },
};
</script>
<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss). Layout
// calcado de AssignAnalysts (dos columnas + bandeja) con las dos pantallas
// de verificación server-driven encima.
.consolidated-modal {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
    width: min(940px, 94vw);
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    background-color: var(--ap-paper);
    border-radius: var(--ap-radius);
    overflow: hidden;
}

.modal-card-head {
    border-bottom: 1px solid var(--ap-line);
    flex-shrink: 0;
    gap: 10px;
}

:deep(.modal-card-title){
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--ap-ink);
}

.head-tag {
    flex-shrink: 0;
    padding: 3px 11px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-teal-wash);
    color: var(--ap-teal-deep);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
}

.col-eyebrow {
    margin: 0 0 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
}

.section-body {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    background-color: var(--ap-paper);
}

.col {
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    padding: 16px 22px 18px;
}

.col-members {
    border-right: 1px solid var(--ap-line);
}

.col-scroll {
    flex: 1;
    min-height: 0;
    overflow: hidden auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.search-input {
    width: 100%;
    margin-bottom: 12px;
    padding: 8px 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-sm);
    font-family: var(--ap-font);
    font-size: 13.5px;
    color: var(--ap-ink);
    transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

    &:focus {
        outline: none;
        border-color: var(--ap-teal);
        box-shadow: 0 0 0 3px var(--ap-teal-wash);
    }
}

.company-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-paper);

    &.is-added {
        background-color: var(--ap-ok-wash);
        border-color: rgba(15, 157, 107, 0.25);
    }

    &.is-removed {
        background-color: var(--ap-alert-wash);
        border-color: rgba(192, 69, 59, 0.25);

        .company-row__name {
            text-decoration: line-through;
            text-decoration-thickness: 2px;
            color: var(--ap-alert);
        }
    }
}

.company-row__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.company-row__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ap-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-row__meta {
    font-size: 11.5px;
    color: var(--ap-slate);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.state-tag {
    flex-shrink: 0;
    padding: 2px 9px;
    border-radius: var(--ap-radius-pill);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.state-tag--add {
    background-color: var(--ap-ok-wash);
    color: var(--ap-ok-ink);
}

.row-action {
    flex-shrink: 0;
    padding: 4px 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-slate);
    font-family: var(--ap-font);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
    }
}

.row-action--remove:hover {
    border-color: var(--ap-alert);
    color: var(--ap-alert);
    background-color: var(--ap-alert-wash);
}

.row-action--add {
    border-color: rgba(15, 157, 107, 0.35);
    color: var(--ap-ok-ink);

    &:hover {
        border-color: var(--ap-ok);
        background-color: var(--ap-ok-wash);
    }
}

.empty-state {
    padding: 26px 10px;
    text-align: center;
    color: var(--ap-muted);
    font-size: 13px;
}

// Pantallas de verificación ----------------------------------------------------
.verify-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 18px 22px;
    background-color: var(--ap-paper);
}

.verify-step {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-teal-deep);
}

.verify-title {
    margin: 0 0 4px;
    font-size: 17px;
    font-weight: 700;
    color: var(--ap-ink);
}

.verify-hint {
    margin: 0 0 14px;
    font-size: 13px;
    color: var(--ap-slate);
}

.verify-hint--strong {
    padding: 10px 12px;
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-alert-wash);
    color: var(--ap-alert);
    font-weight: 600;
}

.verify-scroll {
    flex: 1;
}

.verify-warning {
    margin: 12px 0 0;
    padding: 10px 12px;
    border-radius: var(--ap-radius-sm);
    background-color: var(--ap-alert-wash);
    color: var(--ap-alert);
    font-size: 12.5px;
    font-weight: 600;
}

.change-tag {
    margin-left: 6px;
    padding: 1px 8px;
    border-radius: var(--ap-radius-pill);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.change-tag--alta {
    background-color: var(--ap-ok-wash);
    color: var(--ap-ok-ink);
}

.change-tag--baja {
    background-color: var(--ap-alert-wash);
    color: var(--ap-alert);
}

.change-tag--actual {
    background-color: var(--ap-mist);
    color: var(--ap-muted);
}

// Footer -------------------------------------------------------------------------
:deep(.modal-card-foot){
    border-top: 1px solid var(--ap-line);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    flex-shrink: 0;

    .button.is-primary {
        background-color: var(--ap-ink);
        border-color: transparent;
        font-weight: 600;

        &:hover {
            background-color: var(--ap-teal-deep);
        }
    }
}

.pending-tray {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.tray-empty {
    font-size: 12px;
    color: var(--ap-muted);
}

.tray-chip {
    max-width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 11px;
    border-radius: var(--ap-radius-pill);
    border: 1px solid transparent;
    font-family: var(--ap-font);
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
}

.tray-chip--add {
    background-color: var(--ap-ok-wash);
    color: var(--ap-ok-ink);
    border-color: rgba(15, 157, 107, 0.22);
}

.tray-chip--remove {
    background-color: var(--ap-alert-wash);
    color: var(--ap-alert);
    border-color: rgba(192, 69, 59, 0.22);
}

.tray-discard {
    border: none;
    background: transparent;
    padding: 3px 6px;
    font-family: var(--ap-font);
    font-size: 11.5px;
    font-weight: 600;
    color: var(--ap-muted);
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: var(--ap-alert);
    }
}

.foot-actions {
    display: flex;
    gap: 10px;
}

.foot-actions--spread {
    flex: 1;
    justify-content: space-between;
}

@media (max-width: 760px) {
    .section-body {
        grid-template-columns: 1fr;
        overflow: hidden auto;
    }

    .col-members {
        border-right: none;
        border-bottom: 1px solid var(--ap-line);
    }

    .col-scroll {
        max-height: 240px;
    }
}
</style>
